import * as B from '@blueprintjs/core';
import cssEscape from 'css.escape';
import * as I from 'immutable';
import queryString from 'query-string';
import React from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {Filters, SortingRule} from 'react-table';

import AppNav from 'app/components/AppNav';
import {PushNotificationFn} from 'app/components/Notification';
import {ApiFeature, ApiLensImageryRecord, ApiOrderableScene} from 'app/modules/Remote/Feature';
import {
  ApiAlertCountsByFeatureId,
  ApiFeatureCollection,
} from 'app/modules/Remote/FeatureCollection';
import {ApiOrganization, ApiOrganizationUser} from 'app/modules/Remote/Organization';
import {ApiImageryContract, ApiProject} from 'app/modules/Remote/Project';
import {OrderedVisibleColumnConfiguration} from 'app/pages/PropertyOverview/PropertyTableColumnSettings';
import {
  getSelectedFeatures,
  getSelectedPropertyRows,
} from 'app/pages/PropertyOverview/PropertyTableSelection';
import {usePropertyTable} from 'app/pages/PropertyOverview/usePropertyTable';
import {getDefaultPropertyOverviewColumnSettings} from 'app/pages/PropertyOverview/utils';
import {LoggedInUserActions} from 'app/providers/AuthProvider';
import {FeaturesActions, FeaturesStatus} from 'app/providers/FeaturesProvider';
import {
  InvalidateOrderableScenes,
  OrderableScenesLoader,
} from 'app/providers/OrderableScenesProvider';
import {ProjectsActions} from 'app/providers/ProjectsProvider';
import * as C from 'app/utils/constants';
import featureFlags, {isInternalOrgMemberViewingAnyOrg} from 'app/utils/featureFlags';
import * as featureUtils from 'app/utils/featureUtils';
import {CachedApiGetterOptions, StatusMaybe, useStateWithDeps} from 'app/utils/hookUtils';
import {checkFeatureCollectionIsProcessingStatus} from 'app/utils/layerUtils';
import * as routeUtils from 'app/utils/routeUtils';
import {atLeastUserRole, isOwnerOrAdmin} from 'app/utils/userUtils';

import PortfolioDocuments, {DocumentsPage} from './PortfolioDocuments';
import cs from './PropertyOverview.module.scss';
import {PropertyOverviewModals} from './PropertyOverviewModals';
import {PropertyRow, SelectedRowActions, TableControls} from './PropertyTable';
import {PropertyTableUiState} from './PropertyTableTypes';
import PropertyTableView from './PropertyTableView';
import {AddPropertiesOptions} from '../ManageProperties/AddPropertiesOptions';
import {ManagePropertiesWrapper} from '../ManageProperties/ManagePropertiesWrapper/ManagePropertiesWrapper';

import {NoteCountsByFeatureId} from '.';

// Lazy-load the map view so its chunk (mapbox-gl, turf, map components) loads only when
// the user switches to Map. Keeps the table view bundle smaller and shows a spinner
// immediately on tab switch instead of blocking until the map view mounts.
const PropertyMapView = React.lazy(() => import('./PropertyMapView'));

export interface Props {
  pushNotification: PushNotificationFn;

  organization: I.ImmutableOf<ApiOrganization>;
  loggedInUserActions: LoggedInUserActions;
  organizationUsers: ApiOrganizationUser[];
  profile: I.ImmutableOf<ApiOrganizationUser>;
  selectedProject: I.ImmutableOf<ApiProject>;
  selectedFeatureCollection: I.ImmutableOf<ApiFeatureCollection>;

  imageryRecords: StatusMaybe<I.ImmutableOf<ApiLensImageryRecord[]>>;

  userNoteCountsByFeatureId: StatusMaybe<NoteCountsByFeatureId>;
  alertNoteCountsByFeatureId: StatusMaybe<ApiAlertCountsByFeatureId>;
  reportCountsByFeatureId: Record<string, number> | undefined;

  getCurrentImageryContract: (
    projectId: string,
    opts?: CachedApiGetterOptions
  ) => StatusMaybe<ApiImageryContract | null>;

  /** Year is state of PropertyOverviewDataProvider because it affects loading */
  year: number;
  setYear: (year: number) => void;

  features: I.ImmutableOf<ApiFeature[]>;
  featuresStatus: FeaturesStatus;
  featuresActions: FeaturesActions;
  projectsActions: ProjectsActions;

  initialFilterState: Filters<I.ImmutableOf<ApiFeature[]>>;
  initialSortState: SortingRule<I.ImmutableListOf<ApiFeature>>[];
  onFilterStateChange: (filters: Filters<I.ImmutableOf<ApiFeature[]>>) => void;
  onSortStateChange: (sortBy: SortingRule<I.ImmutableListOf<ApiFeature>>[]) => void;
  tableUiStateRef: React.MutableRefObject<PropertyTableUiState>;

  isExpandedForTest?: boolean;

  orderableScenesLoader: OrderableScenesLoader;
  invalidateOrderableScenes: InvalidateOrderableScenes;
  page?: routeUtils.ProjectDashboardPage;
}

/** Type of the map we build from feature ID to imagery notes */
export type ImageryNotificationsByFeatureId = I.Map<
  number,
  I.Iterable<number, I.ImmutableOf<ApiLensImageryRecord>>
>;

/**
 * Lens property overview page.
 *
 * This page maintains some state with the browser to make it bookmarkable and
 * so when you click "back" to it it will be in roughly the same shape that you
 * left it.
 *
 * The state of the table filters is stored in the location’s hash. This makes
 * the filters bookmarkable. We update them with history.replace to avoid adding
 * a bunch of entries to the browser history, however.
 *
 * The focused table row and row selection are kept in tableUiStateRef, which is
 * owned above the table ↔ property-map switch (see MonitoringFilterState). We
 * do this so you can click into a property, then click back and see the table
 * just as you left it. Preserving the focus both helps with keeping your place
 * and also makes keyboard navigation persist.
 */
const PropertyOverview: React.FunctionComponent<React.PropsWithChildren<Props>> = ({
  pushNotification,
  organization,
  organizationUsers,
  loggedInUserActions,
  profile,
  selectedProject,
  selectedFeatureCollection,
  imageryRecords,
  userNoteCountsByFeatureId,
  alertNoteCountsByFeatureId,
  reportCountsByFeatureId,

  year,
  setYear,

  getCurrentImageryContract,

  features,
  featuresStatus,
  featuresActions,
  projectsActions,

  initialFilterState,
  initialSortState,
  onFilterStateChange,
  onSortStateChange,
  tableUiStateRef,

  isExpandedForTest,

  orderableScenesLoader,
  page,
}) => {
  const navigate = useNavigate();
  let displayName: string = selectedProject.get('name');

  // A portfolio without properties shows the add-properties empty state on its
  // own; the tabs appear once there is something to document.
  const isEmptyPortfolio = featuresStatus === 'done' && features.isEmpty();
  const documentsPage: DocumentsPage | undefined =
    page === 'documents' || page === 'shareLinks' ? page : undefined;
  const selectedTab: 'properties' | 'documents' =
    !isEmptyPortfolio && documentsPage ? 'documents' : 'properties';

  // Each tab keeps whichever of its views was last shown, so switching to the
  // other tab and back returns to the same view.
  const propertiesPage = page === 'table' || page === 'map' ? page : undefined;
  const lastPropertiesPageRef = React.useRef(propertiesPage);
  if (propertiesPage !== undefined) {
    lastPropertiesPageRef.current = propertiesPage;
  }
  const lastDocumentsPageRef = React.useRef<DocumentsPage>(documentsPage ?? 'documents');
  if (documentsPage !== undefined) {
    lastDocumentsPageRef.current = documentsPage;
  }

  // Some debug help below where you can show feature IDs in property names
  // when pressing alt + i (internal org members only)
  const [showFeatureIdInTitle, setShowFeatureIdInTitle] = React.useState(false);
  const hotkeys = React.useMemo(
    () => [
      {
        global: true,
        combo: 'alt + i',
        label: 'Toggle feature ID in property names',
        onKeyDown: () => {
          if (isInternalOrgMemberViewingAnyOrg(profile)) {
            setShowFeatureIdInTitle((prev) => !prev);
          }
        },
      },
    ],
    [profile]
  );

  displayName = showFeatureIdInTitle
    ? `${displayName} (PjID: ${selectedProject.get('id')}, FCID: ${selectedFeatureCollection.get('id')})`
    : displayName;

  return (
    <div className={cs.page}>
      <B.HotkeysTarget2 hotkeys={hotkeys}>
        <></>
      </B.HotkeysTarget2>

      <AppNav
        organization={organization}
        profile={profile}
        selectedProject={selectedProject}
        isMinimal={true}
        loggedInUserActions={loggedInUserActions}
        showHotkeys
      >
        <div className={cs.portfolioName}>{displayName}</div>
      </AppNav>

      {!isEmptyPortfolio && (
        <div className={cs.pageHeader}>
          <B.Tabs
            id="portfolioTabs"
            className={cs.pageTabs}
            selectedTabId={selectedTab}
            onChange={(tabId) =>
              navigate(
                routeUtils.makeProjectDashboardUrl(
                  organization.get('id'),
                  selectedProject.get('id'),
                  tabId === 'documents'
                    ? lastDocumentsPageRef.current
                    : lastPropertiesPageRef.current
                )
              )
            }
          >
            <B.Tab id="properties" title="Properties" />
            <B.Tab id="documents" title="Documents" />
          </B.Tabs>
        </div>
      )}

      {selectedTab === 'documents' && documentsPage ? (
        <PortfolioDocuments
          page={documentsPage}
          organization={organization}
          profile={profile}
          selectedProject={selectedProject}
          selectedFeatureCollection={selectedFeatureCollection}
          pushNotification={pushNotification}
        />
      ) : (
        <PropertyOverviewProperties
          organization={organization}
          organizationUsers={organizationUsers}
          imageryRecords={imageryRecords}
          userNoteCountsByFeatureId={userNoteCountsByFeatureId}
          alertNoteCountsByFeatureId={alertNoteCountsByFeatureId}
          reportCountsByFeatureId={reportCountsByFeatureId}
          profile={profile}
          year={year}
          selectedProject={selectedProject}
          selectedFeatureCollection={selectedFeatureCollection}
          getCurrentImageryContract={getCurrentImageryContract}
          features={features}
          featuresStatus={featuresStatus}
          featuresActions={featuresActions}
          loggedInUserActions={loggedInUserActions}
          projectsActions={projectsActions}
          initialFilterState={initialFilterState}
          initialSortState={initialSortState}
          onFilterStateChange={onFilterStateChange}
          onSortStateChange={onSortStateChange}
          tableUiStateRef={tableUiStateRef}
          isExpandedForTest={isExpandedForTest}
          setYear={setYear}
          orderableScenesLoader={orderableScenesLoader}
          page={propertiesPage}
          showFeatureIdInTitle={showFeatureIdInTitle}
        />
      )}
    </div>
  );
};

const PropertyOverviewProperties = React.memo(
  ({
    organization,
    organizationUsers,
    selectedProject,
    selectedFeatureCollection,
    imageryRecords,
    userNoteCountsByFeatureId,
    alertNoteCountsByFeatureId,
    reportCountsByFeatureId,
    profile,
    year,
    getCurrentImageryContract,
    features,
    featuresStatus,
    featuresActions,
    loggedInUserActions,
    projectsActions,
    initialFilterState,
    initialSortState,
    onFilterStateChange,
    onSortStateChange,
    tableUiStateRef,
    isExpandedForTest,
    setYear,
    orderableScenesLoader,
    page,
    showFeatureIdInTitle,
  }: React.PropsWithChildren<{
    organization: I.ImmutableOf<ApiOrganization>;
    organizationUsers: ApiOrganizationUser[];
    selectedProject: I.ImmutableOf<ApiProject>;
    selectedFeatureCollection: I.ImmutableOf<ApiFeatureCollection>;
    imageryRecords: StatusMaybe<I.ImmutableListOf<ApiLensImageryRecord>>;
    userNoteCountsByFeatureId: StatusMaybe<NoteCountsByFeatureId>;
    alertNoteCountsByFeatureId: StatusMaybe<ApiAlertCountsByFeatureId>;
    reportCountsByFeatureId: Record<string, number> | undefined;
    profile: I.MapAsRecord<I.ImmutableFields<ApiOrganizationUser>>;
    year: number;
    getCurrentImageryContract: (
      projectId: string,
      opts?: CachedApiGetterOptions
    ) => StatusMaybe<ApiImageryContract | null>;
    features: I.ImmutableOf<ApiFeature[]>;
    featuresStatus: FeaturesStatus;
    featuresActions: FeaturesActions;
    loggedInUserActions: LoggedInUserActions;
    projectsActions: ProjectsActions;
    initialFilterState: Filters<I.ImmutableOf<ApiFeature[]>>;
    initialSortState: SortingRule<I.ImmutableListOf<ApiFeature>>[];
    onFilterStateChange: (filters: Filters<I.ImmutableOf<ApiFeature[]>>) => void;
    onSortStateChange: (sortBy: SortingRule<I.ImmutableListOf<ApiFeature>>[]) => void;
    tableUiStateRef: React.MutableRefObject<PropertyTableUiState>;
    isExpandedForTest?: boolean;
    setYear: (year: number) => void;
    orderableScenesLoader: OrderableScenesLoader;
    page?: 'table' | 'map';
    showFeatureIdInTitle: boolean;
  }>) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isViewModePending, startViewModeTransition] = React.useTransition();
    const organizationId = organization.get('id');
    const projectId = selectedProject.get('id');
    const featureCollectionId = selectedFeatureCollection.get('id');
    const layersProcessing = checkFeatureCollectionIsProcessingStatus(selectedFeatureCollection);

    // Use URL parameter for view mode, defaulting to 'table' if not specified
    // Gate map view behind PORTFOLIO_MAP_VIEW feature flag
    const canUseMapView = featureFlags.PORTFOLIO_MAP_VIEW(organization, profile);
    const viewMode: 'table' | 'map' = page === 'map' && canUseMapView ? 'map' : 'table';

    const sortedOrganizationUsers = React.useMemo(
      () => organizationUsers.sort((a, b) => featureUtils.alphanumericSort(a.name, b.name)),
      [organizationUsers]
    );

    // Features are combined by name, and then sorted. Each group is sorted by
    // feature ID for consistency.
    const groupedFeatures = React.useMemo<I.ImmutableOf<ApiFeature[]>[]>(
      () =>
        features
          .filter((f) => !f!.getIn(['properties', 'isArchived']))
          .groupBy((f) => f!.getIn(['properties', 'name']))
          .valueSeq()
          .map((seq) => seq!.sortBy((f) => f!.get('id')).toList())
          .sortBy(
            (seq) => seq!.first().getIn(['properties', 'name']),
            featureUtils.alphanumericSort
          )
          .toArray(),
      [features]
    );

    // these are the properties that came from the original feature upload.
    // we want to provide them as options for additional columns.
    // what's in this list varies WIDELY by organization and may contain not-very-friendly property names.
    const additionalFeatureProperties = React.useMemo(() => {
      if (!features) return I.Set<string>([]);
      return features
        .flatMap((f) =>
          f!
            .get('properties')
            .keySeq()
            .filter((x) => !featureUtils.FeatureAttributes.all.includes(x!))
            .toSet()
        )
        .sort(featureUtils.alphanumericSort)
        .toSet();
    }, [features]);
    // Convert our list of records about available imagery into a by-ID map.
    const imageryByFeatureId = React.useMemo<ImageryNotificationsByFeatureId | null>(() => {
      switch (imageryRecords.status) {
        case 'unknown':
          return null;
        case 'error':
          return I.Map();
        case 'some':
          return imageryRecords.value.groupBy((n) => n!.get('feature_id')).toMap();
      }
    }, [imageryRecords]);

    const defaultSelectedRowIds = React.useMemo<Record<string, boolean>>(
      () =>
        tableUiStateRef.current.selectedRowIds.reduce((acc, key) => ({...acc, [key]: true}), {}),
      [tableUiStateRef]
    );

    const [featureIdsForImageryDrawer, setFeatureIdsForImageryDrawer] = useStateWithDeps(
      I.Set<number>(),
      [selectedFeatureCollection.get('id')]
    );

    const [activeFeatureIdForImageryDrawer, setActiveFeatureIdForImageryDrawer] = useStateWithDeps<
      number | null
    >(null, [selectedFeatureCollection.get('id')]);

    const role = profile.get('role');

    const allowSelection = role !== C.USER_ROLE_READONLY;

    const showImageryPurchaseDrawer = React.useCallback(
      (features: I.ImmutableOf<ApiFeature[]>, activeFeatureId: number | null) => {
        setFeatureIdsForImageryDrawer(I.Set(features.map((f) => f!.get('id'))));
        setActiveFeatureIdForImageryDrawer(activeFeatureId);
      },
      [setFeatureIdsForImageryDrawer, setActiveFeatureIdForImageryDrawer]
    );

    const openCustomizeTagsModal = React.useMemo(
      () => (isOwnerOrAdmin(role) ? () => setIsCustomizeTagsModalOpen(true) : null),
      [role]
    );

    const [isUpdatingFeatures, setIsUpdatingFeatures] = React.useState(false);

    // which columns are visible in what order in the table
    const [columnSettings, setColumnSettings] = React.useState<OrderedVisibleColumnConfiguration>(
      getDefaultPropertyOverviewColumnSettings(organization)
    );

    const {table} = usePropertyTable({
      year,
      groupedFeatures,
      imageryByFeatureId,
      userNoteCountsByFeatureId,
      alertNoteCountsByFeatureId,
      reportCountsByFeatureId,
      organizationUsers: sortedOrganizationUsers,
      organization,
      projectId,
      featureCollection: selectedFeatureCollection,
      allowSelection,
      savedFilterState: initialFilterState,
      savedSortState: initialSortState,
      savedSelectedRowIds: defaultSelectedRowIds,
      showImageryPurchaseDrawer,
      batchPatchFeature: featuresActions.batchPatchFeature,
      openCustomizeTagsModal,
      isExpandedForTest,
      setYear,
      columnSettings,
      showFeatureIdInTitle,
      additionalFeatureProperties,
    });

    // dynamic grid template column styles based on visible columns
    const gridTemplateColumns = React.useMemo(() => {
      const visibleColumnCount = table.columns.length;

      const remainingColumns = visibleColumnCount - 2; // Everything except selection and name columns
      // selection checkbox gets a fixed width (5.6rem), name gets some extra space, then the rest of the columns
      // 5.6rem is twice our subrow indent -- we slightly offset selection+name in subrows via css margrins while keeping everything else aligned
      return `5.6rem minmax(16rem, 2fr) repeat(${remainingColumns}, minmax(18rem, 1fr))`;
    }, [table.columns.length]);

    const tableContainerRef = React.useRef<HTMLDivElement>(null);

    const fullQueryString = React.useMemo(() => {
      const sortByQueryString = queryString.stringify(
        table.state.sortBy.reduce((state, {id, desc}) => ({...state, [id]: desc}), {})
      );
      const filterQueryString = queryString.stringify(
        table.state.filters.reduce((state, {id, value}) => ({...state, [id]: value}), {})
      );
      return filterQueryString + (sortByQueryString ? '&sortBy' + sortByQueryString : '');
    }, [table.state.filters, table.state.sortBy]);

    React.useEffect(() => {
      const currentHash = location.hash;
      if (currentHash !== fullQueryString) {
        navigate(
          {
            pathname: location.pathname,
            search: location.search,
            hash: fullQueryString,
          },
          {replace: true}
        );
      }
    }, [
      navigate,
      location.pathname,
      location.search,
      table.state.filters,
      table.state.sortBy,
      fullQueryString,
      location.hash,
    ]);

    React.useEffect(() => {
      onFilterStateChange(table.state.filters);
    }, [onFilterStateChange, table.state.filters]);

    React.useEffect(() => {
      onSortStateChange(table.state.sortBy);
    }, [onSortStateChange, table.state.sortBy]);

    React.useEffect(() => {
      tableUiStateRef.current.selectedRowIds = Object.entries(table.state.selectedRowIds)
        .filter(([, val]) => val)
        .map(([key]) => key);
    }, [table.state.selectedRowIds, tableUiStateRef]);

    // Corrects for a react-table bug where all the "child" sub-rows may be
    // unselected but the "parent" row is still selected.
    React.useEffect(() => {
      table.selectedFlatRows.forEach((row) => {
        if (row.isExpanded && !row.subRows.some((subRow) => subRow.isSelected)) {
          row.toggleRowSelected();
        }
      });
    }, [table.selectedFlatRows]);

    const itemData: Parameters<typeof PropertyRow>[0]['data'] = React.useMemo(
      () => ({
        table,
        allowSelection,
        organizationId: organization.get('id'),
        projectId,
        featureCollectionId,
        gridTemplateColumns,

        onRowFocus(row) {
          tableUiStateRef.current.focusedRowId = row.id;
        },
        onRowBlur(row) {
          if (tableUiStateRef.current.focusedRowId === row.id) {
            tableUiStateRef.current.focusedRowId = null;
          }
        },
      }),
      [
        table,
        allowSelection,
        organization,
        projectId,
        featureCollectionId,
        gridTemplateColumns,
        tableUiStateRef,
      ]
    );

    // Function to run after the first render to restore the focused row, if there
    // was one.
    const onItemsRenderedRef = React.useRef<Function | null>(async () => {
      const lastFocusedRowId = tableUiStateRef.current.focusedRowId;

      const rowEl =
        lastFocusedRowId &&
        document.querySelector(`.${cs.row}[data-id=${cssEscape(lastFocusedRowId)}]`);

      if (rowEl) {
        (rowEl as HTMLElement).focus();
      }

      // We set the ref to null so that our function only runs after the first
      // render and not subsequent ones.
      onItemsRenderedRef.current = null;
    });

    const selectedPropertyRows = getSelectedPropertyRows(table.flatRows);
    const hasSelectedPropertyRows = selectedPropertyRows.length > 0;

    const selectedFeatures = React.useMemo(
      () => getSelectedFeatures(table.flatRows).toJS() as ApiFeature[],
      [table.flatRows, table.state.selectedRowIds]
    );
    const [orderImageryInfo, setOrderImageryInfo] = React.useState<{
      feature: I.ImmutableOf<ApiFeature>;
      orderableScene: I.ImmutableOf<ApiOrderableScene>;
    } | null>(null);

    const [isCustomizeTagsModalOpen, setIsCustomizeTagsModalOpen] = React.useState(false);

    const [isTaskImageryModalOpen, setIsTaskImageryModalOpen] = React.useState(false);
    const isTaskImageryModalEnabled = atLeastUserRole(C.USER_ROLE_REGULAR, profile);
    const openTaskImageryModal = isTaskImageryModalEnabled
      ? () => setIsTaskImageryModalOpen(true)
      : undefined;

    const projectIdPrefix = routeUtils.makeUuidPrefix(projectId);
    const orgIdPrefix = routeUtils.makeUuidPrefix(organizationId);

    if (featuresStatus === 'done' && features.isEmpty()) {
      const deletePortfolio = () => {
        if (
          confirm(
            `Are you sure you want to delete ${selectedProject.get(
              'name'
            )} portfolio? This will also delete any properties in your portfolio.`
          )
        ) {
          projectsActions.deleteProject(projectId);
          navigate(`/${routeUtils.makeUuidPrefix(organization.get('id'))}/projects`);
        }
      };

      return (
        <ManagePropertiesWrapper>
          <div>
            <h2>No properties yet</h2>
            <div>Add properties to start exploring imagery and data in your portfolio.</div>
          </div>

          {allowSelection ? (
            <AddPropertiesOptions organization={organization} projectId={projectIdPrefix} />
          ) : (
            <B.Callout icon="lock">
              You don’t have permission to add properties. Ask an admin to add you as a Member or
              Admin.
            </B.Callout>
          )}

          <div className={cs.emptyStateLinks}>
            <div className={cs.emptyStateLinkRow}>
              <span>Not ready to add properties yet?</span>
              <a href={`/${orgIdPrefix}/settings/layers`} target="_blank" rel="noopener noreferrer">
                Browse datasets in the Lens Library
              </a>
            </div>

            <div className={cs.emptyStateLinkRow}>
              <span>Need help getting started?</span>
              <a href="https://lens.tech/support/" target="_blank" rel="noopener noreferrer">
                Visit support
              </a>
            </div>
          </div>

          {isOwnerOrAdmin(role) && (
            <B.Button
              intent={'danger'}
              text={'Delete portfolio'}
              onClick={deletePortfolio}
              icon={'trash'}
              minimal
            />
          )}
        </ManagePropertiesWrapper>
      );
    }

    return (
      <>
        <div className={cs.tableControls}>
          {canUseMapView && (
            <B.SegmentedControl
              options={[
                {label: 'Table', value: 'table'},
                {label: 'Map', value: 'map'},
              ]}
              value={viewMode}
              onValueChange={(value) => {
                const newUrl = routeUtils.makeProjectDashboardUrl(
                  organization.get('id'),
                  selectedProject.get('id'),
                  value as 'table' | 'map',
                  undefined,
                  location.hash,
                  {includeRoot: false}
                );
                startViewModeTransition(() => {
                  navigate(newUrl, {replace: true});
                });
              }}
              aria-busy={isViewModePending}
            />
          )}
          <TableControls
            table={table}
            project={selectedProject}
            featureCollection={selectedFeatureCollection}
            organizationUsers={sortedOrganizationUsers}
            columnSettings={columnSettings}
            onColumnSettingsChange={setColumnSettings}
            loggedInUserActions={loggedInUserActions}
            profile={profile}
            additionalFeatureProperties={additionalFeatureProperties}
          />
        </div>

        {layersProcessing && (
          <B.Callout className={cs.callout} intent={B.Intent.WARNING} icon={null}>
            <div style={{display: 'flex', alignItems: 'center'}}>
              <B.Icon icon="time" className={cs.warningIcon} intent="warning" />
              <div className={cs.calloutContent}>Imagery is loading</div>
            </div>
          </B.Callout>
        )}
        {viewMode === 'table' ? (
          <PropertyTableView
            table={table}
            tableContainerRef={tableContainerRef}
            itemData={itemData}
            gridTemplateColumns={gridTemplateColumns}
            featuresStatus={featuresStatus}
            isUpdatingFeatures={isUpdatingFeatures}
            hasSelectedPropertyRows={hasSelectedPropertyRows}
            features={features}
            featureIdsForImageryDrawer={featureIdsForImageryDrawer}
            setFeatureIdsForImageryDrawer={setFeatureIdsForImageryDrawer}
            activeFeatureIdForImageryDrawer={activeFeatureIdForImageryDrawer}
            setActiveFeatureIdForImageryDrawer={setActiveFeatureIdForImageryDrawer}
            organization={organization}
            profile={profile}
            selectedProject={selectedProject}
            selectedFeatureCollection={selectedFeatureCollection}
            organizationUsers={sortedOrganizationUsers}
            projectId={projectId}
            year={year}
            userNoteCountsByFeatureId={userNoteCountsByFeatureId}
            alertNoteCountsByFeatureId={alertNoteCountsByFeatureId}
            reportCountsByFeatureId={reportCountsByFeatureId}
            featuresActions={featuresActions}
            loggedInUserActions={loggedInUserActions}
            orderableScenesLoader={orderableScenesLoader}
            onSetOrderImageryInfo={setOrderImageryInfo}
            setIsUpdatingFeatures={setIsUpdatingFeatures}
            setIsCustomizeTagsModalOpen={setIsCustomizeTagsModalOpen}
            openTaskImageryModal={openTaskImageryModal}
          />
        ) : (
          <>
            {hasSelectedPropertyRows && !isUpdatingFeatures && (
              <div className={cs.selectedRowActionsContainer}>
                <SelectedRowActions
                  project={selectedProject}
                  role={profile.get('role')}
                  rows={table.flatRows}
                  organization={organization}
                  userNoteCountsByFeatureId={userNoteCountsByFeatureId}
                  alertNoteCountsByFeatureId={alertNoteCountsByFeatureId}
                  year={year}
                  organizationUsers={sortedOrganizationUsers}
                  updateFeature={featuresActions.updateFeature}
                  batchPatchFeature={featuresActions.batchPatchFeature}
                  batchArchiveFeatures={featuresActions.batchArchiveFeatures}
                  moveFeatures={featuresActions.moveFeatures}
                  refreshFeatures={featuresActions.refreshFeatures}
                  clearSelection={() => table.toggleAllRowsSelected(false)}
                  selectedFeatureCollection={selectedFeatureCollection}
                  openCustomizeTagsModal={() => setIsCustomizeTagsModalOpen(true)}
                  setIsUpdatingFeatures={setIsUpdatingFeatures}
                  openTaskImageryModal={openTaskImageryModal}
                />
              </div>
            )}
            <React.Suspense
              fallback={
                <div className={cs.tableContainer}>
                  <B.Spinner className={cs.tableLoadingSpinner} />
                </div>
              }
            >
              <PropertyMapView
                hasSelectedPropertyRows={hasSelectedPropertyRows}
                selectedFeatureCollection={selectedFeatureCollection}
                featuresStatus={featuresStatus}
                table={table}
                allowSelection={allowSelection}
                projectId={projectId}
              />
            </React.Suspense>
          </>
        )}
        <PropertyOverviewModals
          orderImageryInfo={orderImageryInfo}
          setOrderImageryInfo={setOrderImageryInfo}
          isCustomizeTagsModalOpen={isCustomizeTagsModalOpen}
          setIsCustomizeTagsModalOpen={setIsCustomizeTagsModalOpen}
          isTaskImageryModalOpen={isTaskImageryModalOpen}
          setIsTaskImageryModalOpen={setIsTaskImageryModalOpen}
          isTaskImageryModalEnabled={isTaskImageryModalEnabled}
          selectedFeatures={selectedFeatures}
          projectId={projectId}
          featureCollectionId={featureCollectionId}
          getCurrentImageryContract={getCurrentImageryContract}
          loggedInUserActions={loggedInUserActions}
        />
      </>
    );
  }
);

export default PropertyOverview;
